/**
 * BrowserPerfReporter (Wave 1.1, 2026-05-15) — closes the
 * "lonely frame-jitter spike" attribution gap.
 *
 * Until now we could measure rAF deltas but couldn't say *why* a 300ms
 * spike happened. With:
 *   - `performance.memory` polled every 500ms → heap pressure + GC inference
 *   - `PerformanceObserver({type:'longtask'})` → exact spans of long tasks
 *   - rAF delta sampling → ground truth for what the user actually saw
 * we can label each spike: "GC pause, 47ms, heap −12MB" vs "longtask,
 * single-frame system overload" vs "neither — pure paint/render delay".
 *
 * Mirrors DiagSnapshotReporter's gate + mount conventions. Mounted from
 * main.tsx (alphabetical with other Reporters). Push every 5s via
 * `netobs:browserPerf` Colyseus message → BrowserPerfBuffer per-session
 * ring → `/api/diag/browser-perf` route → `dj_h2000_browser_perf` MCP tool.
 */

import React, { useEffect } from 'react';
import { isNetobsEnabled } from './isNetobsEnabled';
import type {
  BrowserPerfSampleWire,
  BrowserPerfSnapshotWire,
} from '../../../shared/diagnostics/BrowserPerfTypes';

const REPORT_INTERVAL_MS = 5000;
const HEAP_SAMPLE_INTERVAL_MS = 500;
/** When `usedJSHeapSize` drops by more than this between samples, count
 *  it as a likely GC event. 8MB chosen empirically — natural allocation
 *  gradients in our app are well under this between two 500ms samples. */
const GC_DROP_THRESHOLD_MB = 8;

interface MemoryShape {
  usedJSHeapSize: number;
  jsHeapSizeLimit: number;
  totalJSHeapSize: number;
}

function readMemory(): MemoryShape | null {
  const perf = (globalThis as { performance?: Performance & { memory?: MemoryShape } }).performance;
  if (!perf || !perf.memory) return null;
  return perf.memory;
}

const BrowserPerfReporterInner: React.FC = () => {
  useEffect(() => {
    // ─── Heap samples ────────────────────────────────────────────────
    const heapSamples: BrowserPerfSampleWire[] = [];
    const hasMemory = readMemory() !== null;

    const heapTimer = window.setInterval(() => {
      const m = readMemory();
      const sample: BrowserPerfSampleWire = {
        capturedAt: Date.now(),
        heapUsedMB: m ? m.usedJSHeapSize / 1048576 : NaN,
        heapLimitMB: m ? m.jsHeapSizeLimit / 1048576 : NaN,
      };
      heapSamples.push(sample);
      // Cap at 60 samples (~30s @ 500ms) — way more than the 5s push window.
      if (heapSamples.length > 60) heapSamples.shift();
    }, HEAP_SAMPLE_INTERVAL_MS);

    // ─── Longtask observer ───────────────────────────────────────────
    interface LongtaskEntry { duration: number; startTime: number }
    const longtasks: LongtaskEntry[] = [];
    let longtaskObserver: PerformanceObserver | null = null;
    let hasLongTaskObserver = false;
    try {
      // PerformanceObserver may throw if the entry type is unsupported
      // (Safari before iOS 17). Probe via supportedEntryTypes.
      const ptEntryTypes = (PerformanceObserver as unknown as { supportedEntryTypes?: string[] }).supportedEntryTypes;
      if (ptEntryTypes && ptEntryTypes.includes('longtask')) {
        longtaskObserver = new PerformanceObserver((list) => {
          for (const e of list.getEntries()) {
            longtasks.push({ duration: e.duration, startTime: e.startTime });
          }
          if (longtasks.length > 200) longtasks.splice(0, longtasks.length - 200);
        });
        longtaskObserver.observe({ type: 'longtask', buffered: false });
        hasLongTaskObserver = true;
      }
    } catch {
      hasLongTaskObserver = false;
    }

    // ─── rAF delta sampling ──────────────────────────────────────────
    const rafDeltas: number[] = [];
    let lastRafTs = performance.now();
    let rafHandle = 0;
    const rafTick = (ts: number): void => {
      const dt = ts - lastRafTs;
      lastRafTs = ts;
      if (dt > 0 && dt < 2000) rafDeltas.push(dt);
      if (rafDeltas.length > 600) rafDeltas.splice(0, rafDeltas.length - 600);
      rafHandle = requestAnimationFrame(rafTick);
    };
    rafHandle = requestAnimationFrame(rafTick);

    // ─── Push loop ───────────────────────────────────────────────────
    let lastFlushAt = Date.now();
    const flushTimer = window.setInterval(() => {
      const now = Date.now();
      const windowMs = now - lastFlushAt;
      lastFlushAt = now;

      const inWindowHeap = heapSamples.filter((s) => s.capturedAt >= now - windowMs);
      let heapMax = -Infinity;
      let heapMin = Infinity;
      let heapDropTotalMB = 0;
      let gcLikelyEvents = 0;
      let prev: number | null = null;
      for (const s of inWindowHeap) {
        if (!Number.isFinite(s.heapUsedMB)) continue;
        if (s.heapUsedMB > heapMax) heapMax = s.heapUsedMB;
        if (s.heapUsedMB < heapMin) heapMin = s.heapUsedMB;
        if (prev !== null) {
          const drop = prev - s.heapUsedMB;
          if (drop >= GC_DROP_THRESHOLD_MB) {
            heapDropTotalMB += drop;
            gcLikelyEvents += 1;
          }
        }
        prev = s.heapUsedMB;
      }
      const latestHeap = inWindowHeap[inWindowHeap.length - 1];

      const inWindowLT = longtasks.filter((t) => t.startTime >= performance.now() - windowMs);
      let ltTotal = 0;
      let ltMax = 0;
      for (const t of inWindowLT) {
        ltTotal += t.duration;
        if (t.duration > ltMax) ltMax = t.duration;
      }
      // Trim retained longtasks to entries newer than 2× window.
      const cutoffLT = performance.now() - windowMs * 2;
      while (longtasks.length > 0 && longtasks[0].startTime < cutoffLT) longtasks.shift();

      let rafMean = 0;
      let rafMax = 0;
      // We can't filter rAF deltas by capturedAt — they're just durations —
      // but we trim them to roughly the window's worth on each flush.
      // The deltas accumulated since last flush *should* be the window's deltas.
      const rafInWindow = rafDeltas.slice();
      rafDeltas.length = 0;
      for (const d of rafInWindow) {
        rafMean += d;
        if (d > rafMax) rafMax = d;
      }
      if (rafInWindow.length > 0) rafMean = rafMean / rafInWindow.length;

      const snapshot: BrowserPerfSnapshotWire = {
        capturedAt: now,
        windowMs,
        latestHeapUsedMB: latestHeap ? latestHeap.heapUsedMB : NaN,
        heapLimitMB: latestHeap ? latestHeap.heapLimitMB : NaN,
        heapMaxMB: heapMax === -Infinity ? NaN : heapMax,
        heapMinMB: heapMin === Infinity ? NaN : heapMin,
        heapDropTotalMB,
        gcLikelyEvents,
        heapSampleCount: inWindowHeap.length,
        longtaskCount: inWindowLT.length,
        longtaskTotalMs: ltTotal,
        longtaskMaxMs: ltMax,
        rafDeltaMeanMs: rafMean,
        rafDeltaMaxMs: rafMax,
        rafSampleCount: rafInWindow.length,
        hasPerformanceMemory: hasMemory,
        hasLongTaskObserver,
      };

      try {
        const room = (globalThis as any).nm?.getRoom?.();
        if (room && typeof room.send === 'function') {
          room.send('netobs:browserPerf', snapshot);
        }
      } catch {
        // diagnostic-only, never gameplay
      }
    }, REPORT_INTERVAL_MS);

    return () => {
      window.clearInterval(heapTimer);
      window.clearInterval(flushTimer);
      cancelAnimationFrame(rafHandle);
      if (longtaskObserver) {
        try { longtaskObserver.disconnect(); } catch { /* noop */ }
      }
    };
  }, []);

  return null;
};

export const BrowserPerfReporter: React.FC = () => {
  if (!isNetobsEnabled()) return null;
  return <BrowserPerfReporterInner />;
};
